import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageShell from "../components/PageShell";
import Hero from "../components/Hero";
import LogoMarquee from "../components/LogoMarquee";
import CallToAction from "../components/CallToAction";
import { CASE_LIST } from "../data/cases";

const SERVICE_TEASERS = [
  {
    n: "01",
    title: "Web, Mobile & Desktop Apps",
    body: "Production-grade product engineering — pixel-precise, type-safe, observable from day one.",
  },
  {
    n: "02",
    title: "AI Agents & Workflows",
    body: "Reliable agentic systems, RAG pipelines and LLM-powered internal tools, evaluated and monitored.",
  },
  {
    n: "03",
    title: "Cyber Security",
    body: "Threat modelling, pen-testing and SOC2-aligned hardening for fast-moving teams.",
  },
];

export default function Home() {
  return (
    <PageShell>
      <Hero />
      <LogoMarquee />

      {/* Services teaser */}
      <section className="py-28 md:py-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-20">
            <div>
              <span className="text-[11px] tracking-[0.28em] uppercase text-muted-x font-mono-x">
                ◇ Services
              </span>
              <h2 className="mt-4 font-display text-5xl md:text-7xl font-medium tracking-tight leading-[0.95] max-w-3xl text-primary-x">
                Three pillars. One studio.
              </h2>
            </div>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-sm text-primary-x group"
            >
              All services
              <span className="transition-transform group-hover:translate-x-0.5">→</span>
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {SERVICE_TEASERS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.08, duration: 0.7 }}
                className="rounded-2xl border border-hairline bg-glass p-8 md:p-10 hover:border-hairline-strong transition-colors"
              >
                <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-8">
                  {s.n}
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-primary-x">
                  {s.title}
                </h3>
                <p className="mt-5 text-secondary-x leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Work teaser */}
      <section className="py-28 md:py-40 bg-surface border-y border-hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <span className="text-[11px] tracking-[0.28em] uppercase text-muted-x font-mono-x">
                ◇ Recent Work
              </span>
              <h2 className="mt-4 font-display text-5xl md:text-7xl font-medium tracking-tight leading-[0.95] text-primary-x">
                Shipped <em className="not-italic text-muted-x">recently.</em>
              </h2>
            </div>
            <Link to="/work" className="inline-flex items-center gap-2 text-sm text-primary-x group">
              See all 12
              <span className="transition-transform group-hover:translate-x-0.5">→</span>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {CASE_LIST.slice(0, 2).map((c, i) => (
              <Link
                key={c.slug}
                to={`/work/${c.slug}`}
                data-testid={`home-work-${c.slug}`}
                className="group relative block aspect-[4/3] overflow-hidden rounded-2xl border border-hairline bg-surface-2"
              >
                <motion.img
                  initial={{ scale: 1.04, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 0.85 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.1, delay: i * 0.1 }}
                  src={c.image}
                  alt={c.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="text-[11px] tracking-[0.25em] uppercase text-zinc-300 font-mono-x mb-2">
                    {c.client}
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-white">
                    {c.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CallToAction
        eyebrow="◇ Ready when you are"
        title="Let's build something hard together."
        body="Tell us about the product, system or threat. We respond within 24 hours."
      />
    </PageShell>
  );
}
